import React from 'react';
import { HealthBadge } from './ClientInfoTab.jsx';

const OPP_TONE = {
  Won: 'status-active',
  Active: 'status-active',
  Rejected: 'status-completed',
  Closed: 'status-completed',
};

// Read-only this round. The Relationship overview mirrors what's edited on the
// Client Info tab; Suggested actions and Growth opportunities render whatever
// exists (empty for now) — their editors come in a later round.
export function ClientOutlookTab({ detail, loading }) {
  const { relationship, actions, opportunities } = detail;

  if (loading) return <div className="empty-panel">Loading…</div>;

  return (
    <div className="client-outlook-tab">
      <p className="tab-intro">
        Strategic, client-level relationship development — not a daily task
        list.
      </p>

      <section className="card">
        <div className="card-head">
          <h2>Relationship overview</h2>
        </div>
        <div className="relationship-grid">
          <div>
            <span>Health</span>
            <b>
              <HealthBadge health={relationship.health} />
            </b>
          </div>
          <div>
            <span>Last meaningful interaction</span>
            <b>{relationship.lastMeaningfulInteraction || '—'}</b>
          </div>
          <div>
            <span>Last strategic review</span>
            <b>{relationship.lastStrategicReview || '—'}</b>
          </div>
          <div>
            <span>Recommended next checkpoint</span>
            <b>{relationship.nextCheckpoint || '—'}</b>
          </div>
          <div>
            <span>Contact cadence</span>
            <b>{relationship.contactCadence || '—'}</b>
          </div>
        </div>
        {relationship.healthNotes.length > 0 && (
          <div className="relationship-reasons">
            {relationship.healthNotes.map((note, i) => (
              <p key={i}>• {note}</p>
            ))}
          </div>
        )}
        <p className="muted-text outlook-edit-hint">
          Edit these on the Client Info tab.
        </p>
      </section>

      <section className="card">
        <div className="card-head">
          <h2>Suggested client actions</h2>
        </div>
        {actions.length === 0 ? (
          <p className="muted-text">
            No suggested actions yet — account-management recommendations
            (roadmap discussion, executive review, reconnect after inactivity).
            Editing arrives in a later round.
          </p>
        ) : (
          <div className="outlook-list">
            {actions.map((action) => (
              <article className="outlook-item" key={action.id}>
                <div className="outlook-item-main">
                  <b>{action.title}</b>
                  {action.reason && <small>Reason: {action.reason}</small>}
                  <small>
                    {[
                      action.timing && `Timing: ${action.timing}`,
                      action.owner && `Owner: ${action.owner}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </small>
                </div>
                <span className="project-status-badge">{action.status}</span>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="card">
        <div className="card-head">
          <h2>Growth opportunities</h2>
        </div>
        {opportunities.length === 0 ? (
          <p className="muted-text">
            No opportunities tracked yet — title, status (Suggested → Won /
            Rejected), evidence, customer need, timing and source projects.
            Editing arrives in a later round.
          </p>
        ) : (
          <div className="outlook-list">
            {opportunities.map((opp) => (
              <article className="outlook-item" key={opp.id}>
                <div className="outlook-item-main">
                  <b>{opp.title}</b>
                  {opp.evidence && <small>Evidence: {opp.evidence}</small>}
                  {opp.customerNeed && (
                    <small>Customer need: {opp.customerNeed}</small>
                  )}
                  {opp.timing && <small>Timing: {opp.timing}</small>}
                </div>
                <span
                  className={`project-status-badge ${OPP_TONE[opp.status] || ''}`}
                >
                  {opp.status}
                </span>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
